import { Combobox, SelectOption, SelectSizeVariant } from '@payaca/ambrosia';
import { FC, useMemo } from 'react';

import { useTranslation } from '@/i18n';

import {
  CustomFieldPickerNode,
  useCustomFieldPickerNodes,
} from './customFieldConditionPickerNodes';

const BREADCRUMB_SEPARATOR = ' › ';

type OptionMetadata = {
  breadcrumb: string[];
  fieldPath: string;
};

type CustomFieldOption = SelectOption<string, OptionMetadata>;

const filterByLabelBreadcrumbOrPath = (
  option: CustomFieldOption,
  query?: string
): boolean => {
  if (!query) return true;
  const needle = query.toLowerCase();
  const haystacks = [
    option.label,
    ...(option.metadata?.breadcrumb ?? []),
    option.metadata?.fieldPath ?? '',
  ].map((s) => s.toLowerCase());
  return haystacks.some((h) => h.includes(needle));
};

const CustomFieldPickerOption: FC<{ option: CustomFieldOption }> = (props) => {
  const { option } = props;
  return (
    <PathOption
      label={option.label}
      breadcrumb={option.metadata?.breadcrumb ?? []}
    />
  );
};

const CustomFieldPickerSelected: FC<{
  selectedOptions: CustomFieldOption[];
}> = (props) => {
  const { selectedOptions } = props;
  const option = selectedOptions[0];
  if (!option) return null;
  return (
    <PathOption
      label={option.label}
      breadcrumb={option.metadata?.breadcrumb ?? []}
    />
  );
};

type CustomFieldPickerProps = {
  /**
   * Currently-selected dotted field path, or `''`/`undefined` for
   * unselected. The Combobox is passed `value || undefined` internally so
   * the empty-state never pretends to match an empty-string option.
   */
  value: string | undefined;
  onChange: (node: CustomFieldPickerNode) => void;
};

/**
 * Searchable picker over every project-role Custom Field. Renders the
 * leaf/fieldset's own `label` as the primary text and the ancestor
 * breadcrumb (e.g. "Installation Details › Survey") as subordinate text,
 * so users can disambiguate identically-named children of different
 * fieldsets. Search matches the label, the breadcrumb, or the raw
 * fieldPath.
 */
const CustomFieldPicker: FC<CustomFieldPickerProps> = (props) => {
  const { value, onChange } = props;
  const t = useTranslation();
  const { nodes, nodesByPath, isLoading } = useCustomFieldPickerNodes();

  const selectOptions: CustomFieldOption[] = useMemo(
    () =>
      nodes.map((node) => ({
        value: node.fieldPath,
        label: node.definition.label,
        metadata: {
          breadcrumb: node.breadcrumb,
          fieldPath: node.fieldPath,
        },
      })),
    [nodes]
  );

  // Make sure the saved value remains selectable even if the underlying
  // field has been deleted or changed type — otherwise the Combobox would
  // not show a selection at all and the user couldn't re-pick or save.
  const selectOptionsWithFallback = useMemo<CustomFieldOption[]>(() => {
    if (value && !nodesByPath.has(value)) {
      return [
        ...selectOptions,
        {
          value,
          label: value,
          metadata: { breadcrumb: [], fieldPath: value },
        },
      ];
    }
    return selectOptions;
  }, [selectOptions, value, nodesByPath]);

  if (isLoading || (nodes.length === 0 && !value)) {
    return (
      <Combobox<string, false, OptionMetadata>
        sizeVariant={SelectSizeVariant.SM}
        options={[]}
        value={undefined}
        multiple={false}
        disabled
        placeholder={t(
          'pipelineGuards.conditions.CUSTOM_FIELD_CONDITION.field.placeholder'
        )}
      />
    );
  }

  return (
    <Combobox<string, false, OptionMetadata>
      sizeVariant={SelectSizeVariant.SM}
      options={selectOptionsWithFallback}
      value={value || undefined}
      multiple={false}
      placeholder={t(
        'pipelineGuards.conditions.CUSTOM_FIELD_CONDITION.field.placeholder'
      )}
      inputPlaceholder="Search by field or fieldset name…"
      filterFunction={filterByLabelBreadcrumbOrPath}
      CustomOption={CustomFieldPickerOption}
      CustomSelected={CustomFieldPickerSelected}
      onChange={(nextValue) => {
        if (nextValue == null) return;
        const match = nodesByPath.get(nextValue);
        if (match) onChange(match);
      }}
    />
  );
};

const PathOption: FC<{ label: string; breadcrumb: string[] }> = (props) => {
  const { label, breadcrumb } = props;
  return (
    <div className="w-full min-w-0">
      <div className="truncate font-medium">{label}</div>
      {breadcrumb.length > 0 && (
        <small className="block truncate text-gray-500">
          {breadcrumb.join(BREADCRUMB_SEPARATOR)}
        </small>
      )}
    </div>
  );
};

export default CustomFieldPicker;
